/**
 * @fileoverview Authentication context for user management with roles
 */

import { createContext, useContext, useState, useEffect } from 'react';
// Note: UserRole enum is defined in templates.ts
// For now, using string literals to avoid TypeScript import issues in JSX file
const UserRole = {
  CERTIFIER: 'certifier',
  RECIPIENT: 'recipient',
  VERIFIER: 'verifier',
  STUDENT: 'student',
};

// Roles that have full admin access (case-insensitive comparison)
const ADMIN_ROLES = ['certifier', 'admin', 'administrator', 'superadmin'];

/**
 * Centralized, case-insensitive admin check.
 * Any role in ADMIN_ROLES or is_superadmin/isSuperAdmin flag = admin.
 */
export const checkIsAdmin = (user) => {
  if (!user) return false;
  if (user.isSuperAdmin || user.is_superadmin) return true;
  const role = (user.role || '').toLowerCase().trim();
  return ADMIN_ROLES.includes(role);
};

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  useEffect(() => {
    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }

    // Sincronizar con el servidor si hay token
    if (token) {
      const syncUser = async () => {
        try {
          const API_URL = import.meta.env.VITE_API_BASE_URL || '/api';
          const response = await fetch(`${API_URL}/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            const updatedUser = { ...data.user, role: data.user.role || data.user.roles?.[0]?.role_name || UserRole.CERTIFIER };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        } catch (error) {
          console.error('Error syncing user:', error);
        }
      };
      syncUser();
    }
  }, []);

  const login = async (userData, token) => {
    const userWithRole = {
      ...userData,
      role: userData.role || userData.roles?.[0]?.role_name || UserRole.CERTIFIER,
      avatarUrl: userData.avatarUrl || userData.avatar_url,
    };
    setUser(userWithRole);
    localStorage.setItem('user', JSON.stringify(userWithRole));
    if (token) {
      setToken(token);
      localStorage.setItem('auth_token', token);
    }

    // Save current language to DB if user doesn't have one
    const currentLang = localStorage.getItem('preferred_lang');
    if (currentLang && (!userData.preferred_language_code && !userData.preferredLanguage)) {
      try {
        const API_URL = import.meta.env.VITE_API_BASE_URL || '/api';
        await fetch(`${API_URL}/auth/profile`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ preferred_language_code: currentLang })
        });
      } catch (error) {
        console.error('Error auto-saving language on login:', error);
      }
    }
  };

  const updateUser = (updates) => {
    if (!user) return;
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const isCertifier = () => hasRole(UserRole.CERTIFIER);
  const isRecipient = () => hasRole(UserRole.RECIPIENT);
  const isVerifier = () => hasRole(UserRole.VERIFIER);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        updateUser,
        isAuthenticated: !!user,
        hasRole,
        isCertifier,
        isRecipient,
        isVerifier,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

